export type CopilotBrand = {
  id: string
  name: string
  urlPatterns: string[]
}

export type CopilotBrandScope = {
  unrestricted: boolean
  brands: CopilotBrand[]
  deniedUrlPatterns: string[]
  deniedBrandNames: string[]
}

export const BRAND_ACCESS_DENIED =
  "You can only view SharePoint documents for your assigned brands. This answer used documents outside that access, so it was withheld."

export function scopeCopilotQuestion(text: string, scope: CopilotBrandScope | null) {
  if (!scope || scope.unrestricted) return text
  const names = scope.brands.map((brand) => brand.name).filter(Boolean)
  const allowed = names.length ? names.join(", ") : "none"
  return [
    `Access scope: answer only from SharePoint documents for these assigned brands: ${allowed}.`,
    "Do not open, quote, summarize, or cite documents for any other brand.",
    "If the question needs a brand outside this list, say the user does not have access to that brand.",
    "",
    text,
  ].join("\n")
}

export function copilotBrandActivityValue(scope: CopilotBrandScope | null) {
  if (!scope || scope.unrestricted) return { unrestricted: true }
  return {
    unrestricted: false,
    allowedBrands: scope.brands.map((brand) => brand.name),
    allowedUrlPatterns: scope.brands.flatMap((brand) => brand.urlPatterns),
  }
}

function decoded(value: string) {
  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}

function includesPattern(url: string, pattern: string) {
  const needle = decoded(pattern).replace(/\+/g, " ").toLowerCase()
  if (!needle) return false
  return decoded(url).toLowerCase().includes(needle)
}

function nameMatches(name: string, text: string) {
  const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
  return new RegExp(`(^|[^a-z0-9])${escaped}([^a-z0-9]|$)`, "i").test(text)
}

export function citationIsAllowed(
  citation: { title: string; url?: string },
  scope: CopilotBrandScope | null
) {
  if (!scope || scope.unrestricted) return true
  const url = citation.url || ""
  if (url && scope.deniedUrlPatterns.some((pattern) => includesPattern(url, pattern))) {
    return false
  }
  if (!url && scope.deniedBrandNames.some((name) => nameMatches(name, citation.title))) {
    return false
  }
  const allowedPatterns = scope.brands.flatMap((brand) => brand.urlPatterns)
  if (url && /sharepoint\.com/i.test(url) && allowedPatterns.length) {
    return allowedPatterns.some((pattern) => includesPattern(url, pattern))
  }
  if (url && /sharepoint\.com/i.test(url) && !allowedPatterns.length) return false
  return true
}

export function restrictCopilotReply<T extends { text: string; citations: { title: string; url?: string }[] }>(
  reply: T,
  scope: CopilotBrandScope | null
): T {
  if (!scope || scope.unrestricted) return reply
  const citations = reply.citations.filter((citation) => citationIsAllowed(citation, scope))
  const droppedCitation = citations.length !== reply.citations.length
  const mentionsDeniedBrand = scope.deniedBrandNames.some((name) => nameMatches(name, reply.text))
  const mentionsAllowedBrand = scope.brands.some((brand) => nameMatches(brand.name, reply.text))
  if (droppedCitation || (mentionsDeniedBrand && !mentionsAllowedBrand)) {
    return { ...reply, text: BRAND_ACCESS_DENIED, citations: [] }
  }
  return { ...reply, citations }
}
